import type { ParticipantPropertiesPayload } from '@zoom/videosdk'
import { SubsessionStatus, SubsessionUserStatus } from 'zoom/subsessionSdkEnums'
import { useState, useEffect, useCallback } from 'react'
import { ZoomSDKClient, ZoomParticipant } from 'zoom/types'
import { Subsession, CurrentSubsession } from 'zoom/types/subsession'
import { useMount } from './useMount'
import { usePrevious } from './usePrevious'
import { useParticipantsChange } from './useParticipantsChange'
import { useSubsessionOptions } from './useSubsessionOptions'

/* cspell:words Subsession */

// Zoom Video SDK event constants for subsession handling
const SUBSESSION_EVENTS = {
    STATE_CHANGE: 'subsession-state-change',
    MAIN_SESSION_USER_UPDATED: 'main-session-user-updated',
    SUBSESSION_USER_UPDATE: 'subsession-user-update'
} as const

type UseSubsessionOptionsState = ReturnType<typeof useSubsessionOptions>[0]
type UseSubsessionOptionsActions = ReturnType<typeof useSubsessionOptions>[1]

export interface UseSubsessionReturn {
    subsessions: Subsession[]
    subsessionStatus: SubsessionStatus
    subsessionOptions: UseSubsessionOptionsState
    userStatus: SubsessionUserStatus
    currentSubsession: CurrentSubsession
    unassignedUserList: ZoomParticipant[]
    setSubsessionOptions: UseSubsessionOptionsActions
}

/**
 * useSubsession Hook
 *
 * Manages the state synchronization for Zoom Video SDK Subsessions.
 * This hook acts as a monitor, keeping the React state in sync with SDK events.
 */
export function useSubsession (zmClient: ZoomSDKClient | undefined, ssClient: any): UseSubsessionReturn {
    const [subsessions, setSubsessions] = useState<Subsession[]>([])
    const [subsessionStatus, setSubsessionStatus] = useState<SubsessionStatus>(SubsessionStatus.NotStarted)
    const [userStatus, setUserStatus] = useState<SubsessionUserStatus>(SubsessionUserStatus.Initial)
    const [currentSubsession, setCurrentSubsession] = useState<CurrentSubsession>({
        subsessionName: '',
        subsessionId: '',
        userStatus: SubsessionUserStatus.Initial
    })
    const [unassignedUserList, setUnassignedUserList] = useState<ZoomParticipant[]>([])
    const [options, setOptions] = useSubsessionOptions()
    const previousSubsessions = usePrevious(subsessions)

    const onParticipantsChange = useCallback(
        (_participants: any, currentUpdates?: ParticipantPropertiesPayload[]) => {
            if (ssClient) {
                if (subsessionStatus !== SubsessionStatus.NotStarted) {
                    const subsessionList = ssClient.getSubsessionList()
                    if (subsessionList) {
                        setSubsessions(subsessionList)
                    }
                }
                if (currentUpdates && currentUpdates.length > 0) {
                    setUserStatus(ssClient.getUserStatus())
                    setCurrentSubsession(ssClient.getCurrentSubsession())
                }
            }
        },
        [ssClient, subsessionStatus]
    )

    const onSubsessionStateChange = useCallback(
        ({ status }: any) => {
            setSubsessionStatus(status)
            if (ssClient) {
                setUserStatus(ssClient.getUserStatus())
                setSubsessions(ssClient.getSubsessionList())
                setCurrentSubsession(ssClient.getCurrentSubsession())
                const subsessionOptions = ssClient.getSubsessionOptions()
                if (setOptions && subsessionOptions) {
                    setOptions.setRoomOptions?.(subsessionOptions)
                }
            }
        },
        [ssClient, setOptions]
    )

    const onMainSessionUserChange = useCallback(() => {
        if (ssClient) {
            setSubsessions(ssClient.getSubsessionList())
        }
    }, [ssClient])

    useParticipantsChange(zmClient, onParticipantsChange)

    const onSubsessionUserUpdate = useCallback((payload: any) => {
        const { userId, subsessionId, audio, muted, bVideoOn, sharerOn, bShareAudioOn, isTalking } = payload
        setSubsessions(prevSubsessions => prevSubsessions.map(room => {
            if (room.subsessionId === subsessionId) {
                return {
                    ...room,
                    userList: room.userList.map(user => {
                        if (user.userId === userId) {
                            return { ...user, audio, muted, bVideoOn, sharerOn, bShareAudioOn, isTalking }
                        }
                        return user
                    })
                }
            }
            return room
        }))
    }, [])

    useEffect(() => {
        if (zmClient) {
            zmClient.on(SUBSESSION_EVENTS.STATE_CHANGE, onSubsessionStateChange)
            zmClient.on(SUBSESSION_EVENTS.MAIN_SESSION_USER_UPDATED, onMainSessionUserChange)
            zmClient.on(SUBSESSION_EVENTS.SUBSESSION_USER_UPDATE, onSubsessionUserUpdate)
            return () => {
                zmClient.off(SUBSESSION_EVENTS.STATE_CHANGE, onSubsessionStateChange)
                zmClient.off(SUBSESSION_EVENTS.MAIN_SESSION_USER_UPDATED, onMainSessionUserChange)
                zmClient.off(SUBSESSION_EVENTS.SUBSESSION_USER_UPDATE, onSubsessionUserUpdate)
            }
        }
    }, [zmClient, onMainSessionUserChange, onSubsessionStateChange, onSubsessionUserUpdate])

    useEffect(() => {
        if (ssClient && previousSubsessions && previousSubsessions !== subsessions) {
            const assignedUserList = subsessions.reduce((prev: any[], subsession: Subsession) => {
                const attendees = subsession.userList.map(u => u.userId)
                prev.push(...attendees)
                return prev
            }, [])
            let unassignedList = ssClient.getUnassignedUserList()
            if (subsessionStatus === SubsessionStatus.NotStarted || subsessionStatus === SubsessionStatus.Closed) {
                unassignedList = unassignedList.filter((u: any) => !assignedUserList.includes(u.userId))
            }
            setUnassignedUserList(unassignedList)
        }
    }, [ssClient, subsessions, previousSubsessions, subsessionStatus])

    useMount(() => {
        if (ssClient) {
            const status = ssClient.getSubsessionStatus()
            setSubsessionStatus(status)
            setSubsessions(ssClient.getSubsessionList())
            setCurrentSubsession(ssClient.getCurrentSubsession())
            const subsessionOptions = ssClient.getSubsessionOptions()
            if (setOptions && subsessionOptions) {
                setOptions.setRoomOptions?.(subsessionOptions)
            }
        }
    })

    return {
        subsessions,
        subsessionStatus,
        subsessionOptions: options,
        userStatus,
        currentSubsession,
        unassignedUserList,
        setSubsessionOptions: setOptions
    }
}
